import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  useTheme,
  alpha,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  LocalShipping as TruckIcon,
  Verified as VerifiedIcon,
  FileCopy as DocumentIcon,
  QrCode2 as QRIcon,
  Download as DownloadIcon,
  NotificationsActive as NotifyIcon,
  CloudSync as SyncIcon,
  Shield as ShieldIcon,
  Assessment as ReportIcon,
  Schedule as ScheduleIcon,
  AutoAwesome as PremiumIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Image imports (replace with your own)
import ewayDashboard from "./Images/EwayDashboard.png";
import ewayGenerate from "./Images/EwayGenerate.png";
import ewayTrack from "./Images/accounting.png";
import ewayMobile from "./Images/EwayMobile.png";

const EWayBill = () => {
  const theme = useTheme();

  const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: 20,
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
  }));

  const features = [
    {
      title: "Instant E-Way Bill Generation",
      description:
        "Generate E-Way Bills instantly with auto-filled GST details.",
      icon: <DocumentIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Vehicle & Transport Integration",
      description: "Link vehicles and transporters with one click.",
      icon: <TruckIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "QR & Barcode Support",
      description: "Auto-generate QR codes for verification and tracking.",
      icon: <QRIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Expiry & Alerts",
      description: "Get notified before expiry or validation deadlines.",
      icon: <NotifyIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Multi-Format Export",
      description: "Export bills in PDF, Excel, and XML formats.",
      icon: <DownloadIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Real-Time Validation",
      description: "Auto-validate E-Way Bill number with GSTN APIs.",
      icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Box
      sx={{
        pb: 8,
        background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<PremiumIcon />}
                label="E-Way Bill Automation"
                color="primary"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Simplify E-Way Bill Management
              </Typography>

              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Generate, track, and manage all your E-Way Bills in one place —
                with full GST compliance and real-time updates from GSTN.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DocumentIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }}
                >
                  Generate E-Way Bill
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                >
                  Watch Demo
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={ewayDashboard}
                alt="E-Way Bill Dashboard"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Powerful Tools for{" "}
            <Box component="span" color="primary.main">
              E-Way Bill Compliance
            </Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Stay compliant with GSTN while saving time with automation and
            instant verification tools.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <PremiumCard>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      width: 70,
                      height: 70,
                      mb: 3,
                      mx: "auto",
                      border: `2px solid ${alpha(feature.color, 0.2)}`,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </PremiumCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Integration Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <PremiumCard>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ mb: 1, textAlign: "center" }}
          >
            Fully Integrated with{" "}
            <span style={{ color: "#1976d2" }}>RiteAccount</span>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Auto-sync E-Way Bill data with invoices, ledgers, and GST reports.
          </Typography>
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 6 }}>
              <List>
                {[
                  "Auto-link invoices to E-Way Bills",
                  "Sync transporter and vehicle details",
                  "Real-time tracking and validation",
                  "Reconciliation with GSTR-1 & GSTR-3B",
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "h6",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={ewayGenerate}
                alt="Generate E-Way Bill"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>
          </Grid>
        </PremiumCard>
      </Container>

      {/* Mobile E-Way Bill */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={ewayMobile}
              alt="E-Way Bill Mobile"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 3 }}>
              Manage <span style={{ color: "#1976d2" }}>E-Way Bills</span> on
              the Go
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Generate, verify, and share E-Way Bills from your smartphone or
              tablet with secure cloud sync.
            </Typography>

            <Stack spacing={2}>
              {[
                "Instant vehicle updates",
                "Offline generation support",
                "E-Way Bill cancellation from mobile",
                "QR scan to verify authenticity",
                "Notifications for validity & expiry",
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <SyncIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6" fontWeight={500}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <PremiumCard>
          <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>
            Ready to Automate Your E-Way Bills?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Simplify compliance, reduce manual errors, and ensure every shipment
            is GST-compliant.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              }}
              startIcon={<TruckIcon />}
            >
              Start Generating E-Way Bills
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ px: 5, py: 1.5, fontWeight: 600 }}
            >
              Schedule Demo
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            ✓ GSTN Connected • ✓ Real-time Validation • ✓ Multi-User Access
          </Typography>
        </PremiumCard>
      </Container>
    </Box>
  );
};

export default EWayBill;
